import React, { useRef, useEffect, useState } from 'react';
import { CurrencyInput, GetRequest } from 'components/common';
import SectionMenuLayout, { activeSubsection } from 'components/quote_builder_widget/shared/SectionMenuLayout';
import styles from 'components/quote_builder_widget/settings/_style.module.scss';

const UNIT_OPTIONS = [
  { value: 'both', label: 'quote_builder_widget.unit_option_both' },
  { value: 'mm', label: 'quote_builder_widget.unit_option_mm' },
  { value: 'in', label: 'quote_builder_widget.unit_option_in' },
];

const LANGUAGE_OPTIONS = [
  { value: 'auto', label: 'quote_builder_widget.language_option_auto' },
  { value: 'en', label: 'quote_builder_widget.language_option_en' },
  { value: 'tr', label: 'quote_builder_widget.language_option_tr' },
];

const CONFIGURATION_SECTIONS = [
  { id: 'configuration_domains', label: 'quote_builder_widget.domains' },
  { id: 'configuration_unit', label: 'quote_builder_widget.unit_option' },
  { id: 'configuration_language', label: 'quote_builder_widget.language_option' },
  { id: 'configuration_currency', label: 'quote_builder_widget.currency' },
];

// The currencies /mcc's Currency dropdown lists (CurrencyFormField).
const CurrencySelect = ({ currency, setCurrency }) => {
  const [currencies, setCurrencies] = useState({ recent: [], all: [] });

  useEffect(() => {
    GetRequest({
      url: '/quote-builder/currencies',
      useCache: true,
      callback: (data) => { setCurrencies({ recent: data.recent, all: data.all }); },
    });
  }, []);

  return (
    <CurrencyInput
      labelRecent=""
      recent={currencies.recent}
      all={currencies.all}
      value={currency}
      class_name={styles.unitSelect}
      onChange={(event) => setCurrency(event.target.value)}
    />
  );
};

const ConfigurationSection = ({
  activeSection,
  onSectionChange,
  domains,
  setDomains,
  unitOption,
  setUnitOption,
  languageOption,
  setLanguageOption,
  currency,
  setCurrency,
  addDomainFormVisible,
  setAddDomainFormVisible,
  addDomainFormHighlighted,
  setAddDomainFormHighlighted,
  newDomain,
  setNewDomain,
}) => {
  const domainInputRef = useRef();

  useEffect(() => {
    if (addDomainFormVisible && domainInputRef.current) {
      domainInputRef.current.focus();
    }
  }, [addDomainFormVisible]);

  const onlyUnique = (value, index, self) => {
    return self.indexOf(value) === index;
  };

  const addToDomains = () => {
    if (newDomain.split('.').length === 1) {
      return;
    }
    setDomains((previous) => previous.concat([newDomain]).filter(onlyUnique));
    setAddDomainFormHighlighted(false);
    setAddDomainFormVisible(false);
    setNewDomain('');
  };

  const removeFromDomains = (domain) => {
    setDomains((previous) => previous.filter((d) => d !== domain));
  };

  const domainWithHttp = (domain) => `https://${domain}`;
  const active = activeSubsection(CONFIGURATION_SECTIONS, activeSection);

  return (
    <SectionMenuLayout
      label={t('quote_builder_widget.section_configuration')}
      sections={CONFIGURATION_SECTIONS}
      activeId={active.id}
      onSectionChange={onSectionChange}
    >
      {/* Domains */}
      {active.id === 'configuration_domains' && (
        <div className={styles.fieldGroup}>
          <label className={styles.label}>{t('quote_builder_widget.domains')}</label>
          <p className={styles.helpText}>{t('quote_builder_widget.domains_info')}</p>

          <div className={addDomainFormHighlighted ? styles.domainHighlighted : undefined}>
            {domains.length > 0 && (
              <div className={styles.domainList}>
                {domains.map((domain) => (
                  <div key={domain} className={styles.domainItem}>
                    <span className={styles.domainDisplay}>
                      {domainWithHttp(domain)}
                    </span>
                    <button
                      type="button"
                      className={`btn btn-sm ${styles.domainDeleteButton}`}
                      onClick={() => removeFromDomains(domain)}
                    >
                      <i className="fa fa-trash-o" aria-hidden="true"></i> {t('quote_builder_widget.remove')}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {!addDomainFormVisible && (
              <button
                className="btn simple-btn"
                onClick={(event) => {
                  event.preventDefault();
                  setAddDomainFormVisible(true);
                }}
              >
                <i className="fa fa-plus-square-o" aria-hidden="true"></i> {t('quote_builder_widget.add_domain')}
              </button>
            )}

            {addDomainFormVisible && (
              <div className={`form-inline ${styles.addDomainForm}`}>
                <div className="form-group">
                  <div className="input-group">
                    <div className="input-group-addon">https://</div>
                    <input
                      type="text"
                      className="form-control"
                      size="35"
                      ref={domainInputRef}
                      value={newDomain}
                      placeholder="www.example.com"
                      onChange={(event) => setNewDomain(event.target.value)}
                    />
                  </div>
                </div>
                <button type="button" className={`btn simple-btn ${styles.addDomainSaveButton}`} onClick={addToDomains}>
                  {t('quote_builder_widget.save')}
                </button>
                <button className={`btn ${styles.addDomainCancelButton}`} type="button" onClick={() => setAddDomainFormVisible(false)}>
                  {t('quote_builder_widget.cancel')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Unit Selection */}
      {active.id === 'configuration_unit' && (
        <div className={styles.fieldGroup}>
          <label className={styles.label}>{t('quote_builder_widget.unit_option')}</label>
          <p className={styles.helpText}>{t('quote_builder_widget.unit_option_info')}</p>
          <select
            className={`form-control ${styles.unitSelect}`}
            value={unitOption}
            onChange={(event) => setUnitOption(event.target.value)}
          >
            {UNIT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {t(option.label)}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Language Selection */}
      {active.id === 'configuration_language' && (
        <div className={styles.fieldGroup}>
          <label className={styles.label}>{t('quote_builder_widget.language_option')}</label>
          <p className={styles.helpText}>{t('quote_builder_widget.language_option_info')}</p>
          <select
            className={`form-control ${styles.unitSelect}`}
            value={languageOption}
            onChange={(event) => setLanguageOption(event.target.value)}
          >
            {LANGUAGE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {t(option.label)}
              </option>
            ))}
          </select>
        </div>
      )}

      {active.id === 'configuration_currency' && (
        <div className={styles.fieldGroup}>
          <label className={styles.label}>{t('quote_builder_widget.currency')}</label>
          <p className={styles.helpText}>{t('quote_builder_widget.currency_info')}</p>
          <CurrencySelect currency={currency} setCurrency={setCurrency} />
        </div>
      )}
    </SectionMenuLayout>
  );
};

export default ConfigurationSection;
